import React, { FC, useCallback } from 'react';
import { connect } from 'react-redux';

import { DatetimeFormatType, DatetimeMonthFormat, TypeOfDate } from 'app/lib/datetime/types';
import type { ReduxState, ThunkDispatchType } from 'app/redux/types';

import Datetime from 'app/design-system/components/Datetime';
import { useTranslation } from 'app/lang';
import { SlotType } from 'app/lib/betting/betting-entities';
import { replacingHyphenOnDash } from 'app/lib/replaceDashSymbol';
import LiveBadge, { BadgePreset } from 'app/widgets/LiveBadge';

import { RemoveOneClickBetEntry, removeOneClickBetEntry } from '../../../actions/oneClickBet';
import { BetErrorCode, StakeErrorCode, SubmissionState } from '../../../constants';
import type { RecordOfEntryErrorType } from '../../../records';
import { selectProcessingEntry } from '../../../selectors/oneClickBet';
import { selectSubmission } from '../../../selectors/submission';
import Error from '../../EntryComponents/Error';
import Name from '../../EntryComponents/Name';
import RemoveButton from '../../EntryComponents/RemoveButton';
import PriceDiff from '../../PriceDiff';
import Submission from '../../Submission';
import TextWithAmountBar from '../../TextWithAmountBar';

import dictionary from './Entry.dictionary.json';

import entryStyles from '../../EntryComponents/EntryComponents.module.scss';
import styles from './OneClickBetEntry.module.scss';

// ---

type DispatchProp = {
  dispatch: ThunkDispatchType<RemoveOneClickBetEntry>;
};

type StoreProps = {
  disabled: boolean;
  betId?: number;
  betName?: string;
  stakeName?: string;
  matchName?: string;
  matchStartDate?: Date;
  entryPrice?: number;
  entityPrice?: number;
  error?: RecordOfEntryErrorType | null;
  isLive?: boolean;
};

const OneClickBetEntry: FC<DispatchProp & StoreProps> = ({
  disabled,
  betId,
  betName,
  stakeName,
  matchName,
  matchStartDate,
  entryPrice,
  entityPrice,
  error,
  isLive,
  dispatch,
}) => {
  const { t } = useTranslation();
  const removeEntryHandler = useCallback(() => {
    dispatch(removeOneClickBetEntry());
  }, []);

  if (!betId) {
    return null;
  }

  const hasError = !!error?.code;
  const isPriceChangedError = error?.code === BetErrorCode.PRICE_CHANGED;

  const renderBadges = () => {
    if (hasError && !disabled) {
      return null;
    }

    return (
      <div className={styles.badges}
           data-test="oneClickBetEntry-badges">
        {isLive && (
          <LiveBadge preset={BadgePreset.BET_LIVE} />
        )}
        <Datetime value={matchStartDate}
                  data-test="oneClickBetEntry-datetime"
                  className={entryStyles.datetime}
                  format={DatetimeFormatType.LONG}
                  typeOfDate={TypeOfDate.MATCH_DATE}
                  monthFormat={DatetimeMonthFormat.SHORT} />
      </div>
    );
  };

  const renderError = (): string | JSX.Element => {
    const isMinLimitError = error?.code === StakeErrorCode.LIMIT_IS_EXCEEDED;

    if (isMinLimitError && error?.limits) {
      return (
        <TextWithAmountBar hasError
                           value={error.limits.min}
                           data-test="oneClickBetEntry-error"
                           label={dictionary.label} />
      );
    }

    return (
      <Error error={error}
             data-test="oneClickBetEntry-error" />
    );
  };

  const replacedMatchName = replacingHyphenOnDash(matchName);

  return (
    <div className={styles.container}
         data-test="oneClickBetEntry-container">

      <header className={styles.header}
              data-test="oneClickBetEntry-header">
        <Name value={betName || t(dictionary.unknownBetName, { betId })}
              data-test="oneClickBetEntry-betName"
              className={entryStyles.entryName}
              error={hasError} />

        <RemoveButton disabled={disabled}
                      onClick={removeEntryHandler}
                      data-test="oneClickBetEntry-removeButton" />
      </header>

      <main className={styles.main}
            data-test="oneClickBetEntry-main">
        <Name value={stakeName}
              data-test="oneClickBetEntry-stakeName"
              className={entryStyles.stakeName}
              error={hasError} />

        {!(hasError && !disabled && !isPriceChangedError) && (
          <PriceDiff data-test="oneClickBetEntry-priceDiff"
                     prev={entryPrice}
                     next={entityPrice} />
        )}
      </main>

      <Name value={replacedMatchName}
            data-test="oneClickBetEntry-matchName"
            className={entryStyles.matchName}
            error={hasError} />

      {renderBadges()}

      {!disabled && hasError && (
        <Error message={renderError()}
               data-test="oneClickBetEntry-error" />
      )}
      <Submission isOneClickBet />
    </div>
  );
};

const mapStateToProps = (state: ReduxState): StoreProps => {
  const entry = selectProcessingEntry(state);

  const disabled = selectSubmission(state) !== SubmissionState.AWAITING;

  const props: StoreProps = {
    disabled,
  };

  if (entry) {
    props.betId = entry.bet.entity.id;
    props.betName = entry.bet.entity.names.get('long');
    props.stakeName = entry.stake.entity.names.get('long');
    props.matchName = entry.match.entity.name;
    props.matchStartDate = entry.match.entity.startDate;
    props.entryPrice = entry.bet.price;
    props.entityPrice = entry.bet.price;
    props.error = entry.betInfo.error;
    props.isLive = entry.match.entity.slotType === SlotType.LIVE;

    if (!disabled) {
      props.entityPrice = entry.bet.entity.price;
    }
  }

  return props;
};

export default connect(mapStateToProps)(OneClickBetEntry);

